import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Check, X, Sparkles, Zap, Crown, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';

function Pricing() {
  const plans = [
    {
      name: 'Free Forever',
      icon: Sparkles,
      price: '0',
      period: 'month',
      description: 'Start with selected free classroom resources',
      features: [
        { text: 'Access to selected free worksheets', included: true },
        { text: 'Access to selected free test papers', included: true },
        { text: 'AI Worksheet Generator (5/month)', included: true },
        { text: 'AI Test Paper Generator (5/month)', included: true },
        { text: 'Read all Teacher Tips', included: true },
        { text: 'Weekly teaching newsletter', included: true },
        { text: 'Download free resources', included: true },
        { text: 'Community access', included: true },
        { text: 'AI Lesson Planner', included: false },
        { text: 'AI Assessment Hub', included: false }
      ],
      cta: 'Start Free',
      popular: false,
      current: false,
      audience: 'teacher'
    },
    {
      name: 'Parent',
      icon: Heart,
      price: '10',
      period: 'month',
      description: 'Support your child\'s learning at home',
      trial: '3-day free trial',
      features: [
        { text: '3-day free trial (no card required)', included: true, bold: true },
        { text: 'Access to Parent Portal', included: true },
        { text: 'AI Activity Suggester (50/month)', included: true },
        { text: 'AI Topic Explainer for kids', included: true },
        { text: 'Age-appropriate explanations', included: true },
        { text: 'Hands-on learning activities', included: true },
        { text: 'Progress tracking', included: true },
        { text: 'Weekly parent newsletter', included: true },
        { text: 'Educational resources library', included: true },
        { text: 'Priority support', included: true }
      ],
      cta: 'Start Free Trial',
      popular: false,
      current: false,
      audience: 'parent'
    },
    {
      name: 'Teacher Plus',
      icon: Zap,
      price: '10',
      period: 'month',
      description: 'Most Popular - Everything you need for daily teaching',
      features: [
        { text: 'Everything in Free Forever PLUS:', included: true, bold: true },
        { text: '200 AI generations per month', included: true, bold: true },
        { text: 'AI Worksheet Generator', included: true },
        { text: 'AI Test Paper Generator', included: true },
        { text: 'AI Lesson Planner', included: true },
        { text: '✨ AI Activity Suggester (Hands-on ideas with everyday items)', included: true },
        { text: 'Download all generated worksheets & test papers', included: true },
        { text: 'Editable worksheets & test papers', included: true },
        { text: 'Teaching notes & reading resources', included: true },
        { text: 'Classroom differentiation resources', included: true },
        { text: 'Priority support', included: true },
        { text: 'Early access to new resources', included: true }
      ],
      cta: 'Upgrade to Plus',
      popular: true,
      current: false,
      audience: 'teacher'
    },
    {
      name: 'Teacher Pro',
      icon: Crown,
      price: '13',
      period: 'month',
      description: 'All Premium Features + AI Assessment Hub',
      features: [
        { text: 'Everything in Teacher Plus PLUS:', included: true, bold: true },
        { text: 'Unlimited AI generations', included: true, bold: true },
        { text: '✨ AI Activity Suggester', included: true },
        { text: 'AI Assessment Hub (AI-assisted marking)', included: true },
        { text: '  • Upload classroom assessments', included: true },
        { text: '  • AI reads & suggests marks', included: true },
        { text: '  • Teacher review dashboard', included: true },
        { text: '  • Student reports & analytics', included: true },
        { text: 'AI Rubric Generator', included: true },
        { text: 'AI Progress Report Generator', included: true },
        { text: '  • Student progress reports', included: true },
        { text: '  • Performance summaries', included: true },
        { text: '  • Parent-ready reports', included: true },
        { text: 'Premium templates & offline downloads', included: true },
        { text: 'Priority AI processing', included: true },
        { text: 'Priority feature access', included: true }
      ],
      cta: 'Go Pro',
      popular: false,
      current: false,
      audience: 'teacher'
    },
    {
      name: 'School',
      icon: Crown,
      price: 'Custom',
      period: '',
      description: 'For schools and educational institutions',
      features: [
        { text: 'School-wide access', included: true },
        { text: 'Multiple teacher accounts', included: true },
        { text: 'Shared resource library', included: true },
        { text: 'Teacher training & onboarding', included: true },
        { text: 'Usage analytics', included: true },
        { text: 'Dedicated support manager', included: true },
        { text: 'Custom integrations', included: true },
        { text: 'Invoicing & PO support', included: true }
      ],
      cta: 'Contact Us',
      popular: false,
      current: false,
      audience: 'school'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      <section className="py-16 sm:py-24 px-6 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight font-bold text-slate-900 mb-6" data-testid="pricing-title">
              Choose Your Plan
            </h1>
            <p className="text-base text-slate-600 leading-relaxed max-w-2xl mx-auto mb-8">
              Plans for teachers and parents. Start with free resources, upgrade for premium AI tools.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <span className="px-4 py-2 bg-blue-50 text-blue-700 rounded-full font-semibold">
                👩‍🏫 Teacher Plans: Free, Plus, Pro
              </span>
              <span className="px-4 py-2 bg-pink-50 text-pink-700 rounded-full font-semibold">
                👨‍👩‍👧 Parent Plan: Support your child at home
              </span>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 max-w-[1400px] mx-auto">
            {plans.map((plan, index) => {
              const Icon = plan.icon;
              return (
                <div
                  key={index}
                  data-testid={`pricing-card-${index}`}
                  className={`bg-white rounded-2xl p-6 border-2 transition-all duration-300 flex flex-col ${
                    plan.popular
                      ? 'border-blue-600 shadow-[0_8px_30px_rgb(37,99,235,0.15)] lg:scale-105'
                      : plan.audience === 'parent'
                      ? 'border-pink-300 shadow-[0_8px_30px_rgb(236,72,153,0.12)] hover:shadow-[0_8px_30px_rgb(236,72,153,0.2)]'
                      : 'border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)]'
                  }`}
                >
                  {plan.popular && (
                    <div className="bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full inline-block mb-4 self-start">
                      MOST POPULAR
                    </div>
                  )}
                  
                  {plan.trial && (
                    <div className="bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full inline-block mb-4 self-start">
                      {plan.trial.toUpperCase()}
                    </div>
                  )}
                  
                  <div className={`${plan.audience === 'parent' ? 'bg-pink-50' : 'bg-blue-50'} rounded-xl p-2.5 inline-block mb-4 self-start`}>
                    <Icon className={`w-5 h-5 ${plan.audience === 'parent' ? 'text-pink-600' : 'text-blue-600'}`} strokeWidth={1.5} />
                  </div>
                  
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                  <p className="text-sm text-slate-600 mb-4">{plan.description}</p>
                  
                  <div className="mb-6">
                    {plan.price === 'Custom' ? (
                      <div className="text-2xl font-bold text-slate-900">Custom Pricing</div>
                    ) : (
                      <>
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-bold text-slate-900">${plan.price}</span>
                          <span className="text-slate-600">/{plan.period}</span>
                        </div>
                        {(plan.name === 'Teacher Plus' || plan.name === 'Teacher Pro' || plan.name === 'Parent') && (
                          <div className="mt-2 text-sm text-green-600 font-semibold">
                            3-day free trial • No card required
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  
                  <Link
                    to={
                      plan.name === 'Free Forever' ? '/login' :
                      plan.name === 'School' ? '/contact' :
                      plan.name === 'Parent' ? '/payment?plan=parent' :
                      `/payment?plan=${plan.name === 'Teacher Plus' ? 'plus' : 'pro'}`
                    }
                    data-testid={`pricing-cta-${index}`}
                    className={`w-full rounded-xl px-4 py-3 font-medium transition-all active:scale-95 mb-6 block text-center ${
                      plan.popular
                        ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md'
                        : plan.name === 'Parent'
                        ? 'bg-pink-600 text-white hover:bg-pink-700 shadow-sm hover:shadow-md'
                        : 'bg-white text-blue-600 border-2 border-blue-100 hover:border-blue-600 hover:bg-blue-50'
                    }`}
                  >
                    {plan.cta}
                  </Link>
                  
                  <div className="space-y-2.5 flex-grow">
                    {plan.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start gap-2">
                        {feature.included ? (
                          <Check className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                        ) : (
                          <X className="w-4 h-4 text-slate-300 flex-shrink-0 mt-0.5" strokeWidth={2} />
                        )}
                        <span className={`text-xs ${feature.included ? 'text-slate-700' : 'text-slate-400'} ${feature.bold ? 'font-bold' : ''}`}>
                          {feature.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="mt-20 bg-slate-50 rounded-3xl p-12">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                Created by a Teacher. Designed for Every Child to Learn.
              </h3>
              <p className="text-base text-slate-600 max-w-3xl mx-auto leading-relaxed">
                Every worksheet, lesson plan, assessment, and teaching note is created from real classroom experience. Each resource is designed to save teachers time while helping every child learn—including struggling learners who simply need a different approach. AI is available as a helpful assistant, not the main product.
              </p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-2xl border border-slate-100 p-6 text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">Worksheets</div>
                <p className="text-sm text-slate-600">Classroom-tested practice materials</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-100 p-6 text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">Lesson Plans</div>
                <p className="text-sm text-slate-600">Structured, ready-to-teach lessons</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-100 p-6 text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">Assessments</div>
                <p className="text-sm text-slate-600">Tests and evaluation tools</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-100 p-6 text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">Teacher Tips</div>
                <p className="text-sm text-slate-600">Practical strategies, differentiation ideas, and confidence-building activities</p>
              </div>
            </div>
            
            <div className="text-center border-t border-slate-200 pt-8">
              <p className="text-lg text-slate-700 font-semibold mb-2">
                Helping Teachers Save Time. Helping Every Child Learn.
              </p>
              <p className="text-base text-slate-600">
                Because Every Student Deserves an Opportunity to Learn.
              </p>
            </div>
          </div>
          
          <div className="mt-16 text-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              Need help choosing the right plan?
            </h3>
            <p className="text-base text-slate-600 mb-6">
              Whether you're a teacher, tutor, or parent, I'd love to help you find the resources that fit your needs.
            </p>
            <a
              href="mailto:admin@learnwithzeenat.com"
              className="bg-white text-blue-600 border-2 border-blue-100 rounded-xl px-6 py-3 font-medium hover:border-blue-600 hover:bg-blue-50 transition-all active:scale-95 inline-block"
            >
              Contact Me
            </a>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default Pricing;
